import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Link as ScrollLink } from "react-scroll";
import MainLayout from "../../components/MainLayout/MainLayout";
import Images from "../../constants/Images";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCode,
  faServer,
  faDatabase,
  faChartLine,
  faEnvelope,
  faArrowRight,
  faHardHat,
  faLaptopCode,
  faLayerGroup,
  faTools,
} from "@fortawesome/free-solid-svg-icons";
import {
  faReact,
  faJs,
  faNodeJs,
  faGithub,
  faLinkedinIn,
  faCss3,
  faHtml5,
  faBootstrap,
} from "@fortawesome/free-brands-svg-icons";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import "./Home.css";

const Home = () => {
  const typingTextRef = useRef(null);
  const [currentTextIndex, setCurrentTextIndex] = useState(0);

  // Text typing animation
  const textOptions = [
    "Technical Consultant",
    "Software Engineer",
    "Full-Stack Developer",
    "SAP Specialist",
  ];

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut",
      },
    },
  };

  // Intersection observer hooks for scroll animations
  const [techStackInView, techStackInViewRef] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const [strengthsInView, strengthsInViewRef] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const [projectsInView, projectsInViewRef] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const [contactInView, contactInViewRef] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  useEffect(() => {
    // Typing animation effect
    const typewriterInterval = setInterval(() => {
      setCurrentTextIndex((prevIndex) => (prevIndex + 1) % textOptions.length);
    }, 3000);

    return () => clearInterval(typewriterInterval);
  }, [textOptions.length]);

  // Projects data
  const featuredProjects = [
    {
      id: 1,
      title: "Sahii Blog App",
      description:
        "MERN stack blogging platform with React.js, TailwindCSS, Express.js, and MongoDB",
      techs: ["React", "MongoDB", "Express", "Node.js"],
      image: Images.Sahii,
      link: "https://github.com/Mo-Gamer-2000/Sahii-Blog-App",
    },
    {
      id: 2,
      title: "Convo-GPT",
      description:
        "AI Chat Bot with TypeScript, MongoDB, and OpenAI integration",
      techs: ["TypeScript", "OpenAI", "MongoDB", "React"],
      image: Images.ConvoGPT,
      link: "https://github.com/Mo-Gamer-2000/Convo-GPT",
    },
    {
      id: 3,
      title: "Appointify",
      description: "Appointment management system with ASP.NET Core MVC",
      techs: ["C#", "ASP.NET", "SQL", "Bootstrap"],
      image: Images.Appointify,
      link: "https://github.com/Mo-Gamer-2000/AppointmentManagementSystem",
    },
  ];

  return (
    <MainLayout>
      {/* Construction Banner */}
      <div className="construction-banner">
        <FontAwesomeIcon icon={faHardHat} className="construction-icon" />
        <p>🚧 Site Under Construction - Coming Soon 🚧</p>
      </div>

      {/* Hero Section with enhanced animations */}
      <section className="hero-section">
        <motion.div
          className="hero-content"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
        >
          {/* Mobile Image - Will only show on mobile */}
          <motion.div
            className="hero-image-mobile"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="image-container">
              <img
                src={Images.mySelf}
                alt="Moeez Abdul"
                className="profile-image"
              />
              <div className="blob-animation"></div>
            </div>
          </motion.div>

          <motion.div
            className="hero-text"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <motion.h1
              className="greeting"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1 }}
            >
              Hi, I'm <span className="highlight">Moeez</span> 👋🏽
            </motion.h1>

            <motion.h2
              className="typing-text"
              ref={typingTextRef}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 0.2 }}
            >
              I'm a{" "}
              <span className="highlight">{textOptions[currentTextIndex]}</span>
            </motion.h2>

            <motion.p
              className="hero-subtitle"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 0.4 }}
            >
              Transforming complex challenges into streamlined, innovative
              solutions using cutting-edge tech.
            </motion.p>

            <motion.div
              className="hero-cta"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 0.6 }}
            >
              <Link to="/Portfolio" className="primary-btn">
                View Projects <FontAwesomeIcon icon={faArrowRight} />
              </Link>
              <Link to="/CV" className="secondary-btn">
                My Experience <FontAwesomeIcon icon={faArrowRight} />
              </Link>
            </motion.div>

            <motion.div
              className="scroll-indicator"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 0.8 }}
            >
              <ScrollLink
                to="tech-section"
                smooth={true}
                duration={800}
                className="scroll-down"
              >
                <span>Scroll Down</span>
                <motion.span
                  className="scroll-arrow"
                  animate={{ y: [0, 10, 0] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  ↓
                </motion.span>
              </ScrollLink>
            </motion.div>
          </motion.div>

          {/* Desktop Image - Will only show on desktop/tablet */}
          <motion.div
            className="hero-image-desktop"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="image-container">
              <img
                src={Images.mySelf}
                alt="Moeez Abdul"
                className="profile-image"
              />
              <div className="blob-animation"></div>
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* Tech Stack Section */}
      <section
        id="tech-section"
        className="tech-section"
        ref={techStackInViewRef}
      >
        <div className="section-container">
          <motion.h2
            className="section-title"
            initial={{ opacity: 0, y: 20 }}
            animate={
              techStackInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }
            }
            transition={{ duration: 0.6 }}
          >
            My Tech Stack
          </motion.h2>

          <motion.div
            className="tech-container"
            variants={containerVariants}
            initial="hidden"
            animate={techStackInView ? "visible" : "hidden"}
          >
            <motion.div className="tech-category" variants={itemVariants}>
              <h3>
                <FontAwesomeIcon icon={faCode} /> Frontend
              </h3>
              <div className="tech-icons">
                <div className="tech-icon">
                  <FontAwesomeIcon icon={faReact} />
                  <span>React</span>
                </div>
                <div className="tech-icon">
                  <FontAwesomeIcon icon={faJs} />
                  <span>JavaScript</span>
                </div>
                <div className="tech-icon">
                  <FontAwesomeIcon icon={faHtml5} />
                  <span>HTML5</span>
                </div>
                <div className="tech-icon">
                  <FontAwesomeIcon icon={faCss3} />
                  <span>CSS3</span>
                </div>
                <div className="tech-icon">
                  <FontAwesomeIcon icon={faBootstrap} />
                  <span>Bootstrap</span>
                </div>
                <div className="tech-icon">
                  <span className="material-icon">Ts</span>
                  <span>TypeScript</span>
                </div>
              </div>
            </motion.div>

            <motion.div className="tech-category" variants={itemVariants}>
              <h3>
                <FontAwesomeIcon icon={faServer} /> Backend
              </h3>
              <div className="tech-icons">
                <div className="tech-icon">
                  <FontAwesomeIcon icon={faNodeJs} />
                  <span>Node.js</span>
                </div>
                <div className="tech-icon">
                  <span className="asp-icon">C#</span>
                  <span>C#</span>
                </div>
                <div className="tech-icon">
                  <span className="asp-icon">.NET</span>
                  <span>ASP.NET</span>
                </div>
                <div className="tech-icon">
                  <FontAwesomeIcon icon={faDatabase} />
                  <span>SQL</span>
                </div>
                <div className="tech-icon">
                  <span className="api-icon">API</span>
                  <span>RESTful API</span>
                </div>
                <div className="tech-icon">
                  <span className="material-icon">MDB</span>
                  <span>MongoDB</span>
                </div>
              </div>
            </motion.div>

            <motion.div className="tech-category" variants={itemVariants}>
              <h3>
                <FontAwesomeIcon icon={faTools} /> Tools & Others
              </h3>
              <div className="tech-icons">
                <div className="tech-icon">
                  <FontAwesomeIcon icon={faGithub} />
                  <span>Git/GitHub</span>
                </div>
                <div className="tech-icon">
                  <span className="sap-icon">SAP</span>
                  <span>SAP</span>
                </div>
                <div className="tech-icon">
                  <span className="docker-icon">🐳</span>
                  <span>Docker</span>
                </div>
                <div className="tech-icon">
                  <span className="cloud-icon">☁️</span>
                  <span>Cloud</span>
                </div>
                <div className="tech-icon">
                  <span className="agile-icon">📊</span>
                  <span>Agile</span>
                </div>
                <div className="tech-icon">
                  <span className="ux-icon">UX</span>
                  <span>UI/UX</span>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Strengths Section */}
      <section className="strengths-section" ref={strengthsInViewRef}>
        <div className="section-container">
          <motion.h2
            className="section-title"
            initial={{ opacity: 0, y: 20 }}
            animate={
              strengthsInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }
            }
            transition={{ duration: 0.6 }}
          >
            My Strengths
          </motion.h2>

          <motion.div
            className="strengths-container"
            variants={containerVariants}
            initial="hidden"
            animate={strengthsInView ? "visible" : "hidden"}
          >
            <motion.div className="strength-card" variants={itemVariants}>
              <div className="strength-icon">
                <FontAwesomeIcon icon={faLaptopCode} />
              </div>
              <h3>Technical Mastery</h3>
              <p>
                Proficient in JavaScript, C#, ASP.NET, MERN stack, and SAP
                technologies, I craft scalable systems that optimize operations
                and boost productivity.
              </p>
            </motion.div>

            <motion.div className="strength-card" variants={itemVariants}>
              <div className="strength-icon">
                <FontAwesomeIcon icon={faChartLine} />
              </div>
              <h3>Business-Centric Solutions</h3>
              <p>
                I don't just build software; I deliver solutions that drive
                efficiency, align with business objectives, and exceed client
                expectations.
              </p>
            </motion.div>

            <motion.div className="strength-card" variants={itemVariants}>
              <div className="strength-icon">
                <FontAwesomeIcon icon={faLayerGroup} />
              </div>
              <h3>Full-Stack Powerhouse</h3>
              <p>
                From AI-driven projects to enterprise-level systems, I bring a
                holistic, agile approach to every project I work on.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Featured Projects Section */}
      <section className="projects-section" ref={projectsInViewRef}>
        <div className="section-container">
          <motion.h2
            className="section-title"
            initial={{ opacity: 0, y: 20 }}
            animate={
              projectsInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }
            }
            transition={{ duration: 0.6 }}
          >
            Featured Projects
          </motion.h2>

          <motion.div
            className="project-cards"
            variants={containerVariants}
            initial="hidden"
            animate={projectsInView ? "visible" : "hidden"}
          >
            {featuredProjects.map((project) => (
              <motion.div
                key={project.id}
                className="project-card"
                variants={itemVariants}
                whileHover={{
                  y: -10,
                  boxShadow: "0 15px 30px rgba(96, 87, 144, 0.3)",
                }}
                transition={{ duration: 0.3 }}
              >
                <div className="project-card-image">
                  <img src={project.image} alt={project.title} />
                  <div className="project-overlay">
                    <a
                      href={project.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="project-link"
                    >
                      View Project
                    </a>
                  </div>
                </div>
                <div className="project-content">
                  <h3>{project.title}</h3>
                  <p>{project.description}</p>
                  <div className="project-tech">
                    {project.techs.map((tech, index) => (
                      <span key={index}>{tech}</span>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            className="view-more"
            initial={{ opacity: 0 }}
            animate={projectsInView ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <Link to="/Portfolio" className="view-more-btn">
              View All Projects <FontAwesomeIcon icon={faArrowRight} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="contact-section" ref={contactInViewRef}>
        <motion.div
          className="contact-container"
          initial={{ opacity: 0, y: 20 }}
          animate={contactInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6 }}
        >
          <motion.h2
            initial={{ opacity: 0 }}
            animate={contactInView ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Let's Connect
          </motion.h2>

          <motion.p
            initial={{ opacity: 0 }}
            animate={contactInView ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            Interested in working together? Feel free to reach out!
          </motion.p>

          <motion.div
            className="social-links"
            initial={{ opacity: 0 }}
            animate={contactInView ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <motion.a
              href="https://github.com/Mo-Gamer-2000"
              target="_blank"
              rel="noopener noreferrer"
              className="social-link"
              whileHover={{
                y: -5,
                backgroundColor: "#fff",
                color: "rgb(96, 87, 144)",
              }}
              transition={{ duration: 0.3 }}
            >
              <FontAwesomeIcon icon={faGithub} />
            </motion.a>
            <motion.a
              href="https://www.linkedin.com/in/moeez-abdul/"
              target="_blank"
              rel="noopener noreferrer"
              className="social-link"
              whileHover={{
                y: -5,
                backgroundColor: "#fff",
                color: "rgb(96, 87, 144)",
              }}
              transition={{ duration: 0.3 }}
            >
              <FontAwesomeIcon icon={faLinkedinIn} />
            </motion.a>
            <motion.a
              href="mailto:contact@moeezabdul.com"
              className="social-link"
              whileHover={{
                y: -5,
                backgroundColor: "#fff",
                color: "rgb(96, 87, 144)",
              }}
              transition={{ duration: 0.3 }}
            >
              <FontAwesomeIcon icon={faEnvelope} />
            </motion.a>
          </motion.div>

          <motion.a
            href="/CV"
            className="contact-btn"
            initial={{ opacity: 0 }}
            animate={contactInView ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            whileHover={{ y: -3, boxShadow: "0 8px 25px rgba(0, 0, 0, 0.15)" }}
          >
            Download My Resume
          </motion.a>
        </motion.div>
      </section>
    </MainLayout>
  );
};

export default Home;
